// apps/admin/src/pages/whitelist/tabs/WhitelistMerchantsTab.tsx
//
// Who is currently whitelisted for direct card payment, under what ceilings,
// and the controls to add or remove a merchant.
//
// Add and remove both go through the one endpoint that already owns this
// permission — `PUT /admin/merchants/:id/card-payment-permission`, the same
// one the merchant-detail card uses. Adding is `enabled: true`, removing is
// `enabled: false`. There is no second write path for a money-moving
// permission, and no new endpoint was needed for either action.
//
// Removing KEEPS the ceilings. Sending `null` would read as tidier but means
// "no limit", so re-adding the merchant later would silently restore them
// with unlimited card spend — the hole this screen exists to keep shut. And
// the ceilings are not sent at all (`disable_only`): echoing the ones from
// this list overwrote any edit another admin made after it was loaded.

import { useEffect, useMemo, useState, type JSX } from 'react';
import { SearchInput, useToast } from '@printway/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { apiClient, ApiError } from '../../../lib/apiClient.js';
import { Money } from '../../../components/ui/Money.js';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.js';
import { useTranslation } from '../../../i18n/useTranslation.js';
import { useHasPermission } from '../../../stores/useAuthStore.js';
import { PERMISSIONS } from '../../../lib/permissions.js';
import { AddToWhitelistForm } from '../components/AddToWhitelistForm.js';
import { newIdempotencyKey } from '../../../lib/idempotencyKey.js';

interface WhitelistRow {
  merchantId: string;
  name: string;
  email: string | null;
  maxPerOrderCents: number | null;
  maxPerDayCents: number | null;
}

interface WhitelistResponse {
  rows: WhitelistRow[];
  total: number;
  page: number;
  limit: number;
}

const PAGE_SIZE = 25;

/**
 * Prefix shared by EVERY page and search of the whitelist list.
 *
 * Invalidating only the exact key refreshes the view in front of the
 * operator and leaves every other variant stale for its 30s freshness
 * window — so paging back, or clearing the search, shows the merchant that
 * was just removed as still whitelisted.
 */
const LIST_KEY_PREFIX = ['admin', 'whitelist', 'card-payment'] as const;

export function WhitelistMerchantsTab(): JSX.Element {
  const { t } = useTranslation('finance');
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Committed (already debounced) search — `SearchInput` owns the draft.
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [pendingRemove, setPendingRemove] = useState<WhitelistRow | null>(null);
  const [busyMerchantId, setBusyMerchantId] = useState<string | null>(null);

  // Mirrors the server guard on the PUT. `useHasPermission` and NOT the
  // `s.hasPermission` store selector: that selector returns a stable method
  // identity, so the component never re-renders when permissions finish
  // hydrating — on a hard page load the controls stayed hidden from admins
  // who do have the permission (the #2632 shape).
  const hasPermission = useHasPermission();
  const canEdit = hasPermission(PERMISSIONS.BILLING_FRAUD_CONFIG);

  const term = debounced.trim();
  const listQueryKey = useMemo(
    () => [...LIST_KEY_PREFIX, term, page] as const,
    [term, page],
  );

  const query = useQuery<WhitelistResponse, ApiError>({
    queryKey: listQueryKey,
    queryFn: () => {
      const qs = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (term) qs.set('search', term);
      return apiClient.get<WhitelistResponse>(
        `/admin/whitelist/card-payment?${qs.toString()}`,
      );
    },
    staleTime: 30_000,
  });

  // Memoised: a fresh `[]` per render would re-run `existingIds` every render.
  const rows = useMemo(() => query.data?.rows ?? [], [query.data?.rows]);
  const total = query.data?.total ?? 0;
  const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Removing the last row of the last page strands the operator.
  //
  // The server answers for a page that no longer exists, so `rows` is empty —
  // and the empty-state branch below replaces the table AND the pager with
  // it, leaving no control to get back to a page that has data. Clamping here
  // turns that into a normal re-fetch of the last real page.
  //
  // Guarded on a settled query: while one is in flight `maxPage` sits at its
  // default of 1, and clamping against that would yank the operator back to
  // page 1 on every page change.
  useEffect(() => {
    if (!query.isPending && !query.isError && page > maxPage) {
      setPage(maxPage);
    }
  }, [query.isPending, query.isError, page, maxPage]);

  const existingIds = useMemo(
    () => new Set(rows.map((r) => r.merchantId)),
    [rows],
  );

  async function handleRemove(row: WhitelistRow): Promise<void> {
    setBusyMerchantId(row.merchantId);
    try {
      await apiClient.put(
        `/admin/merchants/${encodeURIComponent(row.merchantId)}/card-payment-permission`,
        // Flag only — the server leaves the stored ceilings as they are.
        { enabled: false, disable_only: true },
        { headers: { 'Idempotency-Key': newIdempotencyKey() } },
      );
      // Prefix, not `listQueryKey`: every page and search of this list is now
      // wrong, not just the one on screen. The log gains an audit row, and the
      // merchant-detail card keys this permission per merchant — without it
      // that card kept showing the merchant as whitelisted after removal.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: LIST_KEY_PREFIX }),
        queryClient.invalidateQueries({ queryKey: ['admin', 'whitelist', 'log'] }),
        queryClient.invalidateQueries({
          queryKey: ['admin', 'merchants', row.merchantId, 'card-payment-permission'],
        }),
      ]);
      toast({ variant: 'success', title: t('whitelist.merchants.remove.done') });
    } catch {
      toast({ variant: 'error', title: t('whitelist.merchants.remove.failed') });
    } finally {
      setBusyMerchantId(null);
      setPendingRemove(null);
    }
  }

  return (
    <div className="space-y-4" data-testid="whitelist-merchants-tab">
      <div>
        <h2 className="text-sm font-semibold text-pw-text">
          {t('whitelist.merchants.heading')}
        </h2>
        <p className="mt-1 text-xs text-pw-text-muted">
          {t('whitelist.merchants.hint')}
        </p>
      </div>

      {canEdit ? (
        <AddToWhitelistForm
          existingIds={existingIds}
          listQueryKey={LIST_KEY_PREFIX}
          t={t}
        />
      ) : (
        <p
          className="text-xs text-pw-text-muted"
          data-testid="whitelist-merchants-no-permission"
        >
          {t('whitelist.merchants.noPermission')}
        </p>
      )}

      <SearchInput
        value={debounced}
        onValueChange={(next) => {
          // Untrimmed: SearchInput copies `value` back into the box, so
          // trimming here ate the space the operator was typing ("acme corp"
          // became "acmecorp"). `term` trims for the query instead.
          setDebounced(next);
          // A narrowed result set makes the current page index meaningless.
          setPage(1);
        }}
        placeholder={t('whitelist.merchants.search')}
        aria-label={t('whitelist.merchants.search')}
        className="w-72"
        data-testid="whitelist-merchants-search"
      />

      {query.isPending ? (
        <div
          role="status"
          aria-busy="true"
          className="flex min-h-[20vh] items-center justify-center"
          data-testid="whitelist-merchants-loading"
        >
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-pw-border border-t-pw-primary" />
        </div>
      ) : query.isError ? (
        <div
          className="rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-300"
          data-testid="whitelist-merchants-error"
        >
          {query.error?.message ?? t('whitelist.merchants.loadError')}
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded border border-pw-border bg-pw-surface px-4 py-10 text-center text-sm text-pw-text-muted"
          data-testid="whitelist-merchants-empty"
        >
          {t('whitelist.merchants.empty')}
        </div>
      ) : (
        <>
          <p className="text-xs text-pw-text-muted">
            {t('whitelist.merchants.count', { count: total })}
          </p>
          <div className="overflow-x-auto rounded border border-pw-border">
            <table className="w-full text-sm" data-testid="whitelist-merchants-table">
              <thead className="bg-pw-surface text-left text-xs text-pw-text-muted">
                <tr>
                  <th className="px-3 py-2">{t('whitelist.merchants.colName')}</th>
                  <th className="px-3 py-2">{t('whitelist.merchants.colEmail')}</th>
                  <th className="px-3 py-2 text-right">
                    {t('whitelist.merchants.colPerOrder')}
                  </th>
                  <th className="px-3 py-2 text-right">
                    {t('whitelist.merchants.colPerDay')}
                  </th>
                  {canEdit ? (
                    <th className="px-3 py-2 text-right">
                      {t('whitelist.merchants.colActions')}
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.merchantId}
                    className="border-t border-pw-border"
                    data-testid={`whitelist-row-${r.merchantId}`}
                  >
                    <td className="px-3 py-2">
                      {/* Straight to the form that can change the ceilings —
                          this screen toggles access, that one tunes it. */}
                      <Link
                        to={`/merchants/${encodeURIComponent(r.merchantId)}?tab=wallet`}
                        className="text-pw-primary underline"
                      >
                        {r.name}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-pw-text-muted">{r.email ?? '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {r.maxPerOrderCents === null ? (
                        <span className="text-pw-text-muted">
                          {t('whitelist.merchants.noLimit')}
                        </span>
                      ) : (
                        <Money usdCents={r.maxPerOrderCents} align="right" />
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.maxPerDayCents === null ? (
                        <span className="text-pw-text-muted">
                          {t('whitelist.merchants.noLimit')}
                        </span>
                      ) : (
                        <Money usdCents={r.maxPerDayCents} align="right" />
                      )}
                    </td>
                    {canEdit ? (
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          // Only the row being worked on goes busy; the rest
                          // of the table stays usable.
                          disabled={busyMerchantId === r.merchantId}
                          onClick={() => setPendingRemove(r)}
                          className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-950/40"
                          data-testid={`whitelist-remove-${r.merchantId}`}
                        >
                          {busyMerchantId === r.merchantId
                            ? t('whitelist.merchants.removing')
                            : t('whitelist.merchants.remove.cta')}
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {maxPage > 1 ? (
            <div className="flex items-center gap-3 text-sm">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded border border-pw-border px-3 py-1.5 disabled:opacity-50"
                data-testid="whitelist-merchants-prev"
              >
                ‹
              </button>
              <span className="text-pw-text-muted">
                {page} / {maxPage}
              </span>
              <button
                type="button"
                disabled={page >= maxPage}
                onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
                className="rounded border border-pw-border px-3 py-1.5 disabled:opacity-50"
                data-testid="whitelist-merchants-next"
              >
                ›
              </button>
            </div>
          ) : null}
        </>
      )}

      <ConfirmDialog
        open={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemove(null);
        }}
        type="destructive"
        title={t('whitelist.merchants.remove.confirmTitle')}
        description={t('whitelist.merchants.remove.confirmBody', {
          name: pendingRemove?.name ?? '',
        })}
        confirmLabel={t('whitelist.merchants.remove.confirmCta')}
        loading={busyMerchantId !== null}
        onConfirm={() => {
          if (pendingRemove) void handleRemove(pendingRemove);
        }}
      />
    </div>
  );
}

export default WhitelistMerchantsTab;
